import axios from 'axios';
import {
  ChangeRequestWithDetails,
  CreateChangeRequestData,
  UpdateChangeRequestData,
  ChangeRequestFilters,
  ChangeRequestValidation,
  ChangeRequestResponse,
  ChangeRequestDetailResponse,
  PendingSwapsResponse,
  EligibleSwapsResponse
} from '../types/changeRequest';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

// Create axios instance with auth interceptor
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export class ChangeRequestService {
  // Get all change requests with optional filters
  static async getChangeRequests(filters?: ChangeRequestFilters): Promise<ChangeRequestResponse> {
    const params = new URLSearchParams();
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.request_type) params.append('request_type', filters.request_type);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.start_date) params.append('start_date', filters.start_date);
    if (filters?.end_date) params.append('end_date', filters.end_date);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`/change-requests?${params.toString()}`);
    return response.data;
  }

  // Get a specific change request with details
  static async getChangeRequest(id: number): Promise<ChangeRequestDetailResponse> {
    const response = await apiClient.get(`/change-requests/${id}`);
    return response.data;
  }

  // Create a new change request
  static async createChangeRequest(data: CreateChangeRequestData): Promise<{ message: string; changeRequest: ChangeRequestWithDetails }> {
    try {
      const response = await apiClient.post('/change-requests', data);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 429) {
        throw new Error('Too many requests. Please wait a moment and try again.');
      }
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  // Update a change request
  static async updateChangeRequest(id: number, data: UpdateChangeRequestData): Promise<{ message: string; changeRequest: ChangeRequestWithDetails }> {
    const response = await apiClient.put(`/change-requests/${id}`, data);
    return response.data;
  }

  // Delete/cancel a change request
  static async deleteChangeRequest(id: number): Promise<{ message: string; changeRequest: ChangeRequestWithDetails }> {
    const response = await apiClient.delete(`/change-requests/${id}`);
    return response.data;
  }

  // Approve a change request
  static async approveChangeRequest(id: number, comments?: string): Promise<{ message: string; changeRequest: ChangeRequestWithDetails; warning?: string; updatedSchedule?: any }> {
    const response = await apiClient.post(`/change-requests/${id}/approve`, { comments });
    return response.data;
  }

  // Reject a change request
  static async rejectChangeRequest(id: number, comments: string): Promise<{ message: string; changeRequest: ChangeRequestWithDetails }> {
    const response = await apiClient.post(`/change-requests/${id}/reject`, { comments });
    return response.data;
  }

  // Set request under review
  static async setUnderReview(id: number): Promise<{ message: string; changeRequest: ChangeRequestWithDetails }> {
    const response = await apiClient.post(`/change-requests/${id}/review`);
    return response.data;
  }

  // Accept shift swap request
  static async acceptSwap(id: number): Promise<{ message: string; changeRequest: ChangeRequestWithDetails }> {
    const response = await apiClient.post(`/change-requests/${id}/accept-swap`);
    return response.data;
  }

  // Decline shift swap request
  static async declineSwap(id: number): Promise<{ message: string; changeRequest: ChangeRequestWithDetails }> {
    const response = await apiClient.post(`/change-requests/${id}/decline-swap`);
    return response.data;
  }

  // Get pending swap requests for current user
  static async getPendingSwaps(): Promise<PendingSwapsResponse> {
    const response = await apiClient.get('/change-requests/swaps/pending');
    return response.data;
  }

  // Validate change request before submission
  static async validateChangeRequest(data: CreateChangeRequestData): Promise<ChangeRequestValidation> {
    const response = await apiClient.post('/change-requests/validate', data);
    return response.data;
  }

  // Get eligible schedules for swapping
  static async getEligibleSwaps(scheduleId: number): Promise<EligibleSwapsResponse> {
    const response = await apiClient.get(`/change-requests/schedules/${scheduleId}/eligible-swaps`);
    return response.data;
  }

  // Helper method to format request type for display
  static formatRequestType(requestType: string): string {
    const typeMap: Record<string, string> = {
      'date_change': 'Date Change',
      'time_change': 'Time Change',
      'shift_type_change': 'Shift Type Change',
      'cancellation': 'Cancellation',
      'shift_swap': 'Shift Swap'
    };
    return typeMap[requestType] || requestType;
  }

  // Helper method to get status color
  static getStatusColor(status: string): string {
    const colorMap: Record<string, string> = {
      'pending': '#f59e0b', // amber
      'under_review': '#3b82f6', // blue
      'approved': '#10b981', // green
      'rejected': '#ef4444', // red
      'cancelled': '#6b7280', // gray
      'expired': '#6b7280' // gray
    };
    return colorMap[status] || '#6b7280';
  }

  // Helper method to get priority color
  static getPriorityColor(priority: string): string {
    const colorMap: Record<string, string> = {
      'low': '#10b981', // green
      'normal': '#3b82f6', // blue
      'high': '#ef4444' // red
    };
    return colorMap[priority] || '#6b7280';
  }

  // Helper method to format date for display
  static formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid date';
    }
  }

  // Helper method to format time for display
  static formatTime(dateString: string): string {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // Helper method to check if request is expired
  static isExpired(expiresAt?: string): boolean {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  }

  // Helper method to get time until expiration
  static getTimeUntilExpiration(expiresAt?: string): string {
    if (!expiresAt) return '';
    
    try {
      const now = new Date();
      const expiration = new Date(expiresAt);
      
      if (isNaN(expiration.getTime())) {
        return 'Invalid expiration date';
      }
      
      const diff = expiration.getTime() - now.getTime();
      
      if (diff <= 0) return 'Expired';
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 24) {
        const days = Math.floor(hours / 24);
        return `${days} day${days > 1 ? 's' : ''} remaining`;
      } else if (hours > 0) {
        return `${hours}h ${minutes}m remaining`;
      } else {
        return `${minutes}m remaining`;
      }
    } catch (error) {
      return 'Invalid expiration date';
    }
  }
} 